/**
 * Created by Abner Sui on 05/22/2019.
 * Description:
 *
 * ------ maintenance history ------
 * 09/06/2019  Ella Ma  upgrade ag-grid-angular
 * 12/01/2020  Ella Ma Fix When the grid drop-down scrolls to the next page, the grid scrolling position remains unchanged [TAM-30314]
 */

import { AppState, smartSelector } from '../../redux';
import { ColDef, GridOptions } from 'ag-grid-community';
import { Component, HostListener, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Store, select } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { take, skip } from 'rxjs/operators';

import { DateHelperWebService } from '../../tamalelibs/services/date-helper.web.service';
import { DataList } from '../../tamalelibs/models/data-list.model';
import { FileHelperService } from '../../services/file-helper.service';
import { getFileViewListItemsState, getQueryInfoState, getSortInfoState, getAGGridColumnState, getFileViewListSelectedIndex, getFileViewListCurrentPageIndexState } from '../../redux/reducers/file-view.reducer';
import { QueryInfo, FileViewListConfig } from '../../pages/file-view/file-view.model';
import { ResearchView } from '../../constants/business.constants';
import { FileViewListLoadMore, FileViewListSetSelectedIndex, FileViewListReload, FileViewSetQueryInfo, FileViewSetAGGridColumnStatus } from '../../redux/actions/file-view.actions';
import { DocumentEntry } from '../../tamalelibs/models/document-entry.model';
import { ContextMenuConfig, ContextMenuShowAction } from '../../widgets/tam-context-menu/tam-context-menu.model';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AlertButton, AlertBtnTypes } from '../../widgets/alert-window/alert-window.model';
import { AlertWindowService } from '../../widgets/alert-window/alert-window.service';
import { PriorityStarAggridComponent } from '../../widgets/priority-star-aggrid/priority-star-aggrid.component';
import { DownloadAggridComponent } from '../../widgets/download-aggrid/download-aggrid.component';

export const DEFAULT_PAGE_SIZE = 100;

@Component({
    selector: 'top-file-list',
    templateUrl: './top-file-list.component.html',
    styleUrls: ['./top-file-list.component.scss']
})
export class TopFileListComponent implements OnInit, OnDestroy {
    @Input() config: FileViewListConfig;
    @Input() collapseSuject: Subject<any> = new Subject();

    columnDefs: Array<ColDef>;
    gridOptions: GridOptions;
    loadingMore = false;
    reloading = true;
    selectedIndexes: Set<number> = new Set();
    noDataStyle = 'none';
    fileList: DataList<DocumentEntry> = new DataList<DocumentEntry>();
    contextMenuConfig: ContextMenuConfig;
    isIPAD = false;

    private _destroySubscriptions: Array<Subscription> = [];
    private _currentPageIndex = 1;
    private _columnState: any;
    private _firstLoad = false;
    private _gridColumnApi;
    private _gridApi;
    private _overlayNoRowsTemplate = '<span style=\'font-size: 20px;background-color:white;color:black\'>No Data Found</span>';
    private _queryInfo: QueryInfo;
    private _sortInfo: any;

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _store: Store<AppState>,
        private _fileHelperService: FileHelperService,
        private _deviceService: DeviceDetectorService,
        private _alertWindow: AlertWindowService,
        private _datepipe: DatePipe,
    ) {
        this.isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
    }

    ngOnInit() {
        // Defining data for columns
        this.columnDefs = [
            {
                headerName: 'Download',
                valueGetter: 'data.id',
                cellRenderer: DownloadAggridComponent,
                colId: 'action',
                menuTabs: [],
                minWidth: 96,
                maxWidth: 96,
                sortable: false,
                pinned: 'left',
                suppressMovable: true,
                lockPosition: true,
                lockVisible: true,
                lockPinned: true,
            },
            {
                headerName: 'File Type',
                valueGetter: 'data.extension',
                headerClass: 'icon-header-style',
                cellRenderer: (params) => {
                    const imageElement = document.createElement('img');
                    imageElement.width = 16;
                    imageElement.height = 16;
                    if (params && params.data.extension) {
                        let extension = params.data.extension;
                        if (extension) {
                            extension = extension.toLowerCase();
                        }
                        const iconFileName = this._fileHelperService.getIconFileName(extension);
                        imageElement.src = './assets/images/singlesvg/' + iconFileName;
                        return imageElement;
                    } else {
                        return '';
                    }
                },
                colId: 'extension',
                menuTabs: [],
                minWidth: 64,
                maxWidth: 64,
                sortable: false,
                cellStyle: {
                    'text-align': 'center',
                    'padding-left': '8px',
                    'padding-right': '8px'
                }
            },
            {
                headerName: 'Entities',
                valueGetter: (params) => {
                    let entitiesName = '';
                    params.data.entities.forEach(entity => {
                        entitiesName = entitiesName + entity.shortName + ', ';
                    });
                    return entitiesName.substring(0, entitiesName.length - 2);
                },
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                colId: 'entities',
                menuTabs: ['columnsMenuTab'],
                minWidth: 80,
                sortable: false
            },
            {
                headerName: 'Source',
                valueGetter: 'data.source.shortName',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                colId: 'source',
                menuTabs: ['columnsMenuTab'],
                minWidth: 80,
                // hide: true,
            },
            {
                headerName: 'Submitter',
                valueGetter: 'data.submitter.shortName',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                colId: 'submitter',
                hide: true,
                menuTabs: ['columnsMenuTab'],
                minWidth: 80
            },
            {
                headerName: 'File Name',
                valueGetter: (params) => {
                    return params.data.fileName;
                },
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                colId: 'title',
                menuTabs: ['columnsMenuTab'],
                minWidth: 150,
            },
            {
                headerName: 'Type',
                valueGetter: 'data.type.name',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                colId: 'entrytype',
                menuTabs: ['columnsMenuTab'],
                minWidth: 80,
            },
            {
                headerName: 'Last Modified Date',
                valueGetter: (params) => {
                    return params.data.lastEditDate;
                },
                cellRenderer: (data) => {
                    return data.value ? DateHelperWebService.getWeekdayByDate(data.value) + ' ' + this._datepipe.transform(data.value, 'MM/dd/yyyy h:mm a') : '';
                },
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                colId: 'lastediteddate',
                menuTabs: ['columnsMenuTab'],
                minWidth: 100,
            },
            {
                headerName: 'File Size',
                valueGetter: (params) => {
                    const size: number = params.data.size;
                    if (size > 1024 * 1024) {
                        return (size / (1024 * 1024)).toFixed(2).toString() + 'MB';
                    } else if (size > 1024) {
                        return (size / 1024).toFixed(2).toString() + 'KB';
                    } else {
                        return size.toString() + 'B';
                    }
                },
                headerClass: 'icon-header-style',
                colId: 'size',
                menuTabs: [],
                minWidth: 80,
                maxWidth: 80,
                sortable: false,
                filter: true,
            },
            {
                headerName: 'Sentiment',
                headerComponentParams: {
                    template:
                        '<img src="./assets/images/singlesvg/positive_negative.svg" style="width:16px;height:16px;margin-top:7.5px;"></img>'
                },
                valueGetter: 'data.sentiment',
                headerClass: 'icon-header-style',
                cellRenderer: (params) => {
                    const imageElement = document.createElement('img');
                    imageElement.width = 16;
                    imageElement.height = 16;
                    if (params && params.data && params.data.sentiment) {
                        if (params.data.sentiment === 1) {
                            imageElement.src = './assets/images/singlesvg/positive-header.svg';
                            return imageElement;
                        } else if (params.data.sentiment === -1) {
                            imageElement.src = './assets/images/singlesvg/negative-header.svg';
                            return imageElement;
                        } else {
                            return '';
                        }
                    }
                },
                colId: 'sentiment',
                menuTabs: [],
                minWidth: 32,
                maxWidth: 32,
                sortable: true,
                cellStyle: {
                    'text-align': 'center',
                    'padding-left': '8px',
                    'padding-right': '8px'
                },
                hide: true,
            },
            {
                headerName: 'Display Date',
                valueGetter: (params) => {
                    return params.data.displayDate;
                },
                cellRenderer: (data) => {
                    return data.value ? DateHelperWebService.getWeekdayByDate(data.value) + ' ' + this._datepipe.transform(data.value, 'MM/dd/yyyy h:mm a') : '';
                },
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                colId: 'displaydate',
                menuTabs: ['columnsMenuTab'],
                minWidth: 100,
                sort: 'desc',
                hide: true,
            },
            {
                headerName: 'Submitted Date',
                valueGetter: (params) => {
                    return params.data.submittedDate;
                },
                cellRenderer: (data) => {
                    return data.value ? DateHelperWebService.getWeekdayByDate(data.value) + ' ' + this._datepipe.transform(data.value, 'MM/dd/yyyy h:mm a') : '';
                },
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                hide: true,
                colId: 'submitteddate',
                menuTabs: ['columnsMenuTab'],
                minWidth: 100,
            },
            {
                headerName: 'Priority',
                valueGetter: 'data.priority',
                cellRenderer: PriorityStarAggridComponent,
                colId: 'priority',
                hide: true,
                menuTabs: ['columnsMenuTab'],
                minWidth: 100,
                maxWidth: 100,
                sortable: false,
            },
        ];

        this.gridOptions = {
            debug: false,
            defaultColDef: {
                sortable: true,
                resizable: true
            },
            cacheBlockSize: ResearchView.cacheBlockSize,
            colResizeDefault: 'shift',
            suppressCellFocus: true,
            suppressColumnVirtualisation: true,
            enableBrowserTooltips: true,
            paginationPageSize: DEFAULT_PAGE_SIZE,
            rowSelection: 'single',
            sortingOrder: ['desc', 'asc'],
            maintainColumnOrder: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns: true,
            overlayNoRowsTemplate: this._overlayNoRowsTemplate,
        };

        if (this.collapseSuject) {
            this.collapseSuject.subscribe(event => {
                this._gridColumnAutoFit();
            });
        }
        this._initContextMenu();
        // Changed data in store when conditions change
        this._destroySubscriptions.push(
            this.config.actions$.subscribe(action => this._onSubscribeActions(action)),
        );
        this._firstLoad = true;
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    suppressDefaultContextMenu(event) {
        // agGrid bug, we need to handle this
        event.preventDefault();
        event.stopPropagation();
    }

    onBodyScroll(e) {
        if (e.api.gridBodyCtrl && this.fileList.hasNext && !this.loadingMore) {
            if (e.top + e.api.gridBodyCtrl.eBodyViewport.clientHeight + 1000 > e.api.gridBodyCtrl.eBodyViewport.scrollHeight) {
                this.loadingMore = true;
                this._currentPageIndex++;
                this._store.dispatch(
                    new FileViewListLoadMore(
                        {
                            pageIndex: this._currentPageIndex,
                            pageSize: DEFAULT_PAGE_SIZE,
                            ...this._queryInfo
                        },
                        this.config.prefix,
                        this.config.instanceId
                    )
                );
            }
        }
    }

    onColumnVisible() {
        this._saveState();
    }

    onColumnResized() {
        this._saveState();
    }

    onContextMenu(event) {
        if (this.isIPAD) {
            event.event.clientX = event.event.pageX;
            event.event.clientY = event.event.pageY;
        }
        this.contextMenuConfig.items[0].disabled = (event.data.extension && event.data.extension.toLowerCase() !== 'pdf');
        if (this.isIPAD) {
            this.contextMenuConfig.items[2].disabled = !event.data.deletable;
        } else {
            this.contextMenuConfig.items[3].disabled = !event.data.deletable;
        }
        this._changeDetectorRef.detectChanges();
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(event.event, event.data));
    }

    onDisplayedColumnsChanged(event) {
        event.api.sizeColumnsToFit();
    }

    @HostListener('window:keydown', ['$event'])
    onKeydown(event) {
        // use up/down arrow keys to select through items
        if (event.keyCode === 40) { // down
            // get the index of last selected item
            let nextIndex = 0;
            this.selectedIndexes.forEach(index => {
                if (index > nextIndex) {
                    nextIndex = index;
                }
            });
            if (++nextIndex < this.fileList.length) {
                this._gridApi.forEachNode(function (node) {
                    if (node.rowIndex === nextIndex) {
                        node.setSelected(true);
                    } else {
                        node.setSelected(false);
                    }
                });
            }
        } else if (event.keyCode === 38) { // up
            // get the index of first selected item
            const indexArr = Array.from(this.selectedIndexes.values());
            let nextIndex = indexArr[0];
            indexArr.forEach(index => {
                if (index < nextIndex) {
                    nextIndex = index;
                }
            });
            if (--nextIndex > -1) {
                this._gridApi.forEachNode(function (node) {
                    if (node.rowIndex === nextIndex) {
                        node.setSelected(true);
                    } else {
                        node.setSelected(false);
                    }
                });
            }
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(e) {
        if (!this._gridApi) {
            return;
        }
        this._gridApi.sizeColumnsToFit();
    }

    onRowDoubleClicked(event) {
        // double click didn't handle any data process logic as it will trigger click first
        // and click event handler will process data
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
            return;
        }

        const item = event.api.getSelectedNodes()[0];
        this.config.events$.next({
            type: 'DOUBLE_CLICK',
            payload: item
        });
    }

    onRowSelected(event) {
        if (event.type === 'cellContextMenu') {
            this._gridApi.forEachNode(function (node) {
                if (node.rowIndex === event.rowIndex) {
                    node.setSelected(true);
                }
            });
        }
        const item = event.api.getSelectedNodes()[0];

        if (item) {
            this.selectedIndexes.clear();
            this.selectedIndexes.add(item.rowIndex);
            this._store.dispatch(
                new FileViewListSetSelectedIndex(
                    {
                        index: item.rowIndex,
                        id: item.data.id,
                        threadId: item.data.threadId,
                    },
                    this.config.prefix,
                    this.config.instanceId
                )
            );
        }
    }

    onGridReady(params) {
        this._gridApi = params.api;
        this._gridColumnApi = params.columnApi;
        // Loading data
        this._destroySubscriptions.push(
            this._store.pipe(
                select(this._relativeSelector(getFileViewListItemsState)),
                skip(1),
            ).subscribe(items => this._onSubscribeFileListItem(items))
        );
        this._destroySubscriptions.push(
            this._store.pipe(
                select(this._relativeSelector(getFileViewListSelectedIndex)),
                take(1),
            ).subscribe(selectedIndex => {
                if (selectedIndex) {
                    this.selectedIndexes.clear();
                    this.selectedIndexes.add(selectedIndex.index);
                    this._ensureSelectedItemVisible(selectedIndex.index);
                }
            }));
        this._destroySubscriptions.push(
            this._store.pipe(
                select(this._relativeSelector(getFileViewListCurrentPageIndexState)),
                take(1),
            ).subscribe(currentPageIndex => {
                if (currentPageIndex) {
                    this._currentPageIndex = currentPageIndex;
                }
            }));

        // handle re-initialize during layout change
        this._store.pipe(
            select(this._relativeSelector(getQueryInfoState)),
            take(1)
        ).subscribe(queryInfo => this._queryInfo = queryInfo);

        // get the sort info from store
        this._store.pipe(
            select(this._relativeSelector(getSortInfoState)),
            take(1)
        ).subscribe(sortInfo => this._sortInfo = sortInfo);

        // assign sort info for ag-grid
        let sort = [{
            colId: 'submittedDate',
            sort: 'desc',
        }];
        if (this._sortInfo && JSON.stringify(this._sortInfo) !== '{}') {
            sort = [
                {
                    colId: this._sortInfo.sortBy,
                    sort: this._sortInfo.sortOrder
                }
            ];
        }
        this._gridColumnApi.applyColumnState({ state: sort });
        this._restoreState();
    }

    onPostProcessPopup(params) {
        if (params.type !== 'columnMenu') {
            return;
        }
        // set a fix height for popup column menu
        const ePopup = params.ePopup;
        ePopup.style.top = 0 + 'px';
    }

    onSortChanged(e) {
        this._saveState();
        const columnState = this._gridColumnApi.getColumnState();
        const sortModel = columnState
            .filter(column => column.sort)
            .map(column => ({ colId: column.colId, sort: column.sort }));

        if (sortModel && sortModel.length > 0) {
            // get sort information
            this._queryInfo.sortBy = this._sortInfo.sortBy = sortModel[0].colId;
            this._queryInfo.sortOrder = this._sortInfo.sortOrder = sortModel[0].sort;
            if (!this._firstLoad) {
                // change page index
                this._currentPageIndex = 1;
                // trigger reload
                this._triggerSortChanged(this._sortInfo);
                this.reloading = true;
                this._store.dispatch(
                    new FileViewListReload(
                        {
                            pageIndex: 1,
                            pageSize: DEFAULT_PAGE_SIZE,
                            ...this._queryInfo
                        },
                        this.config.prefix,
                        this.config.instanceId
                    )
                );
            }
            this._firstLoad = false;
        }
    }

    private _deleteFile(data: DocumentEntry) {
        const cancelBtn: AlertButton = {
            text: 'Cancel',
            type: AlertBtnTypes.tertiary
        };
        const confirmButton: AlertButton = {
            text: 'Delete',
            type: AlertBtnTypes.destructive
        };
        const subscription = this._alertWindow.
            custom('Delete confirmation required', ['Are you sure you want to delete this attachment?'], cancelBtn, confirmButton)
            .subscribe((result: boolean) => {
                if (result) {
                    this._store.dispatch({
                        type: 'DELETE_ATTACHMENT',
                        payload: {
                            id: data.id,
                            componentId: null,
                            attachInfo: data,
                        }
                    });
                }
                subscription.unsubscribe();
            });
    }

    private _ensureSelectedItemVisible(index) {
        setTimeout(() => {
            if (index > -1) {
                this._gridApi.forEachNode(function (node) {
                    if (node.rowIndex === index) {
                        node.setSelected(true);
                    }
                });
            }
        });
    }

    private _gridColumnAutoFit() {
        if (this._gridApi) {
            this._gridApi.refreshClientSideRowModel();
            this._gridApi.sizeColumnsToFit();
        }
    }

    private _initContextMenu() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.stateful = false;
        this.contextMenuConfig.items = [
            { id: '0', text: 'Preview', disabled: false },
        ];
        if (!this.isIPAD) {
            this.contextMenuConfig.items.push({ id: '1', text: 'Copy Link', disabled: false });
        }
        this.contextMenuConfig.items.push({ id: '2', text: 'Download', disabled: false });
        this.contextMenuConfig.items.push({ id: '3', text: 'Delete', disabled: false });
        this._destroySubscriptions.push(
            this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event)),
        );
    }

    private _onMenuSelect(event) {
        const type = +event.payload.id;
        const data: DocumentEntry = event.payload.data;
        switch (type) {
            case 0:
                // Preview
                this._preview(data);
                break;
            case 1:
                // Copy link
                this._fileHelperService.copyFileLink(data.id);
                break;
            case 2:
                // Download
                this._fileHelperService.downloadFile(data.fileDataLink + data.id);
                break;
            case 3:
                // Delete
                this._deleteFile(data);
                break;
            default:
                break;
        }

    }

    private _onSubscribeActions(action: any) {
        if (action.type === 'RELOAD') {
            this._reload(action.payload);
        }
    }

    private _onSubscribeFileListItem(items: DataList<DocumentEntry>) {
        // Dispaly no data
        if (items.length === 0) {
            this.noDataStyle = 'flex';
        } else {
            this.noDataStyle = 'none';
        }
        this.fileList = items;
        // reloading the data
        if (this.reloading) {
            this.reloading = false;
            this._gridApi.setRowData(items);
            if (items && items.length > 0) {
                // need to select the first item by default after reload
                const tempSelectedIndex = 0;
                this.selectedIndexes.add(tempSelectedIndex);
                setTimeout(() => {
                    this._gridApi.ensureIndexVisible(tempSelectedIndex, 'top');
                });
                this._gridApi.forEachNode(function (node) {
                    if (node.rowIndex === tempSelectedIndex) {
                        node.setSelected(true); // this will trigger select change
                    }
                });
                this._gridApi.setFocusedCell(tempSelectedIndex, this.columnDefs[0].colId);
            }
        }

        if (this.loadingMore) {
            this.loadingMore = false;
            const startRowIndex = (this._currentPageIndex - 1) * DEFAULT_PAGE_SIZE;
            const endRowIndex = this._currentPageIndex * DEFAULT_PAGE_SIZE;
            this._gridApi.updateRowData({
                add: items.slice(startRowIndex, endRowIndex)
            });
            this.selectedIndexes.forEach(element => {
                const rowNode = this._gridApi.getRowNode(element);
                rowNode.setSelected(true);
            });
        }

        this._gridApi.sizeColumnsToFit();

        // send a notice to parent saying the component is ready
        this.config.events$.next({
            type: 'READY',
            payload: this.fileList.length > 0
        });
    }

    private _preview(data: DocumentEntry) {
        if (data.extension.toLowerCase() === 'pdf') {
            this._fileHelperService.previewFile(data.id, data.fileDataLink, data.extension, data.fileName);
        }
    }

    private _relativeSelector(selector) {
        return (state) => {
            return selector(smartSelector(this.config.instanceId, 'fileViewReducer')(state));
        };
    }

    private _reload(queryInfo) {
        this.reloading = true;
        this._currentPageIndex = 1;
        this.selectedIndexes.clear();
        this._queryInfo = queryInfo;
        this._store.dispatch(
            new FileViewSetQueryInfo(
                this._queryInfo,
                this.config.prefix,
                this.config.instanceId
            )
        );
        this._store.dispatch(
            new FileViewListReload(
                {
                    pageIndex: 1,
                    pageSize: DEFAULT_PAGE_SIZE,
                    ...this._queryInfo
                },
                this.config.prefix,
                this.config.instanceId
            )
        );
    }

    private _restoreState() {
        this._store.pipe(
            select(this._relativeSelector(getAGGridColumnState)),
            take(1)
        ).subscribe(columnState => this._columnState = columnState);

        if (this._columnState && JSON.stringify(this._columnState) !== '{}') {
            this._gridColumnApi.applyColumnState({ state: JSON.parse(this._columnState) });
        }
    }

    private _saveState() {
        this._store.dispatch(
            new FileViewSetAGGridColumnStatus(
                JSON.stringify(this._gridColumnApi.getColumnState()),
                this.config.prefix,
                this.config.instanceId
            )
        );
    }

    private _triggerSortChanged(sortInfo: any) {
        this.config.events$.next({
            type: 'SORTCHANGEED',
            payload: sortInfo,
        });
    }

}
